import {environment} from '../environments/environment';
import {RolePermissionOptions} from './core/model/role';

export const VERSION = '5.1';

export const WEBROOT = environment.apiUrl;
export interface PrivilegeDto {
  __v: number;
  _id: string;
  name: string;
}

export interface Privilege {
  id: string;
  name: string;
  v: number;
}

export enum MenuItemGroupKey {
  Build = 'Build',
  More = 'More',
}

export const MENU_PATHS = {
  CurrentSked: '/current-sked',
  Alerts: '/alerts',
  Goals: '/goals',
  Schedule: '/schedule',
  Projects: '/projects',
  Trackers: '/trackers',
  Tokens: '/tokens',
  Reporting: '/reporting',
  Skills: '/skills',
  Programs: '/programs',
  Wiki: '/wiki',
  Locations: '/locations',
  Rewards: '/rewards',
  Profile: '/profile',
  People: '/people/users',
  Teams: '/people/teams',
  Roles: '/people/roles',
  Lenses: '/people/lenses',
  Billing: '/billing',
  NotificationSettings: '/native-settings/notifications',
};

/**
 * The menuItem rolePermissionKey is used to determine if the menu item should be displayed based on the user's role.
 * If the rolePermissionKey is not defined, the menu item will be displayed for all roles.
 * If the rolePermissionKey is defined, the menu item will only be displayed if the role of the user has the permission activated.
 * See: observeUserRole fn in mobile-nav.component.ts and desktop-sidebar.component.ts
 */
export interface MenuItem {
  display: string;
  url?: string;
  icon?: string;
  adminOnly?: boolean;
  mobileAppOnly?: boolean;
  rolePermissionKey?: RolePermissionOptions;
  itemGroup?: MenuItemGroupKey;
  meta?: {[key: string]: unknown};
}

export type GroupMenuItem = {
  display: string;
  icon: string;
  itemGroup: MenuItemGroupKey;
  children: MenuItem[];
  meta?: {[key: string]: unknown};
};

export type MenuNavItem = MenuItem | GroupMenuItem;
export type MenuNavItems = MenuNavItem[];

export const MENU_ITEMS_MAP = {
  CurrentSked: {
    display: 'Current Sked',
    url: MENU_PATHS.CurrentSked,
    icon: 'sked-list',
    // TBD - rolePermissionKey: RolePermissionOptions.CurrentSked,
  },
  Alerts: {
    display: 'Alerts',
    url: MENU_PATHS.Alerts,
    icon: 'bell',
    itemGroup: MenuItemGroupKey.Build,
    // TBD - rolePermissionKey: RolePermissionOptions.Alerts,
  },
  Planning: {
    display: 'Planning',
  },
  Goals: {
    display: 'Goals',
    url: MENU_PATHS.Goals,
    icon: 'target',
    rolePermissionKey: RolePermissionOptions.Goals,
    itemGroup: MenuItemGroupKey.Build,
  },
  Schedule: {
    display: 'Schedule',
    url: MENU_PATHS.Schedule,
    icon: 'calendar',
    rolePermissionKey: RolePermissionOptions.Schedule,
    itemGroup: MenuItemGroupKey.Build,
  },
  Components: {
    display: 'Components',
  },
  Projects: {
    display: 'Projects',
    url: MENU_PATHS.Projects,
    icon: 'project',
    rolePermissionKey: RolePermissionOptions.Projects,
    itemGroup: MenuItemGroupKey.Build,
  },
  Locations: {
    display: 'Locations',
    url: MENU_PATHS.Locations,
    icon: 'map-pin',
    // TBD - rolePermissionKey: RolePermissionOptions.Locations,
  },
  Trackers: {
    display: 'Trackers',
    url: MENU_PATHS.Trackers,
    icon: 'tracker',
    rolePermissionKey: RolePermissionOptions.Trackers,
    itemGroup: MenuItemGroupKey.Build,
  },
  Rewards: {
    display: 'Rewards',
    url: MENU_PATHS.Rewards,
    icon: 'rewards',
    itemGroup: MenuItemGroupKey.Build,
    // This is open for everyone but inside the rewards page, there are some restrictions
    // rolePermissionKey: RolePermissionOptions.Rewards,
  },
  Tokens: {
    display: 'Tokens',
    url: MENU_PATHS.Tokens,
    icon: 'token',
    itemGroup: MenuItemGroupKey.Build,
    rolePermissionKey: RolePermissionOptions.Tokens,
  },
  Skills: {
    display: 'Skills',
    url: MENU_PATHS.Skills,
    icon: 'star',
    // TBD - rolePermissionKey: RolePermissionOptions.Skills,
  },
  Programs: {
    display: 'Programs',
    url: MENU_PATHS.Programs,
    icon: 'book',
    // TBD - rolePermissionKey: RolePermissionOptions.Programs,
  },
  Reporting: {
    display: 'Reporting',
    url: MENU_PATHS.Reporting,
    icon: 'reporting',
    rolePermissionKey: RolePermissionOptions.Reports,
    itemGroup: MenuItemGroupKey.Build,
  },
  Wiki: {
    display: 'Wiki',
    url: MENU_PATHS.Wiki,
    icon: 'book-open',
    itemGroup: MenuItemGroupKey.Build,
    // TBD - rolePermissionKey: RolePermissionOptions.Wiki,
  },
  Settings: {
    display: 'Settings',
    adminOnly: true,
  },
  People: {
    display: 'People',
    url: MENU_PATHS.People,
    icon: 'user',
    adminOnly: true,
    // TBD - rolePermissionKey: RolePermissionOptions.People,
  },
  MyProfile: {
    display: 'My Profile',
    url: MENU_PATHS.Profile,
    icon: 'smile',
    itemGroup: MenuItemGroupKey.More,
    // TBD - rolePermissionKey: RolePermissionOptions.Profile,
  },
  Teams: {
    display: 'Teams',
    url: MENU_PATHS.Teams,
    icon: 'users',
    itemGroup: MenuItemGroupKey.More,
    adminOnly: true,
    // TBD - rolePermissionKey: RolePermissionOptions.Teams,
  },
  Roles: {
    display: 'Roles',
    url: MENU_PATHS.Roles,
    icon: 'smile',
    itemGroup: MenuItemGroupKey.More,
    adminOnly: true,
    // TBD - rolePermissionKey: RolePermissionOptions.Roles,
  },
  Lenses: {
    display: 'Lenses',
    url: MENU_PATHS.Lenses,
    icon: 'lenses',
    itemGroup: MenuItemGroupKey.More,
    adminOnly: true,
    // TBD - rolePermissionKey: RolePermissionOptions.Lenses,
  },
  NotificationSettings: {
    display: 'Notification Settings',
    url: MENU_PATHS.NotificationSettings,
    icon: 'bell',
    itemGroup: MenuItemGroupKey.More,
    mobileAppOnly: true,
  },
  Billing: {
    display: 'Billing',
    url: MENU_PATHS.Billing,
    icon: 'billing',
    itemGroup: MenuItemGroupKey.More,
    // TBD - rolePermissionKey: RolePermissionOptions.Lenses,
  },
  Logout: {
    display: 'Logout',
    icon: 'logout',
    meta: {isLogout: true},
  },
};

/**
 * @deprecated No longer used in navigation. "Build" has been replaced by a direct "Projects" link.
 * Kept as a record. Use ProjectsRoutingComponent instead.
 */
export const BUILD_MENU_ITEMS: MenuItem[] = [
  MENU_ITEMS_MAP.Goals,
  MENU_ITEMS_MAP.Projects,
  MENU_ITEMS_MAP.Schedule,
  MENU_ITEMS_MAP.Trackers,
  MENU_ITEMS_MAP.Programs,
  MENU_ITEMS_MAP.Wiki,
  MENU_ITEMS_MAP.Tokens,
  MENU_ITEMS_MAP.Rewards,
  MENU_ITEMS_MAP.Reporting,
];

export const MORE_MENU_ITEMS: MenuItem[] = [
  MENU_ITEMS_MAP.MyProfile,
  MENU_ITEMS_MAP.People,
  MENU_ITEMS_MAP.Teams,
  MENU_ITEMS_MAP.Roles,
  MENU_ITEMS_MAP.Skills,
  MENU_ITEMS_MAP.Lenses,
  // MENU_ITEMS_MAP.Billing,
  MENU_ITEMS_MAP.NotificationSettings,
];

export const MENU_NAV_ITEMS: MenuNavItems = [
  {
    display: 'Today',
    icon: 'checkbox-centered',
    url: MENU_PATHS.CurrentSked,
  },
  {
    display: 'Inbox',
    icon: 'bell',
    url: MENU_PATHS.Alerts,
  },
  {
    display: 'Projects',
    icon: 'project',
    url: MENU_PATHS.Projects,
    rolePermissionKey: RolePermissionOptions.Projects,
    meta: {
      relatedUrls: [
        MENU_PATHS.Schedule,
        MENU_PATHS.Rewards,
        MENU_PATHS.Tokens,
        MENU_PATHS.Wiki,
        MENU_PATHS.Trackers,
        MENU_PATHS.Reporting,
      ],
    },
  },
  {
    display: 'Settings',
    icon: 'nut',
    itemGroup: MenuItemGroupKey.More,
    children: MORE_MENU_ITEMS,
  },
];

export const USER_MENU_ITEMS: MenuNavItems = [MENU_ITEMS_MAP.MyProfile, MENU_ITEMS_MAP.Logout];

export const MENU_ITEMS: MenuItem[] = [
  MENU_ITEMS_MAP.CurrentSked,
  MENU_ITEMS_MAP.Alerts,
  MENU_ITEMS_MAP.Planning,
  MENU_ITEMS_MAP.Goals,
  MENU_ITEMS_MAP.Schedule,
  MENU_ITEMS_MAP.Components,
  MENU_ITEMS_MAP.Projects,
  MENU_ITEMS_MAP.Locations,
  MENU_ITEMS_MAP.Trackers,
  MENU_ITEMS_MAP.Rewards,
  MENU_ITEMS_MAP.Tokens,
  MENU_ITEMS_MAP.Skills,
  MENU_ITEMS_MAP.Programs,
  MENU_ITEMS_MAP.Reporting,
  MENU_ITEMS_MAP.Wiki,
  MENU_ITEMS_MAP.Settings,
  MENU_ITEMS_MAP.People,
  MENU_ITEMS_MAP.MyProfile,
  MENU_ITEMS_MAP.Teams,
  MENU_ITEMS_MAP.Roles,
  MENU_ITEMS_MAP.Lenses,
  // TODO: Uncomment when Billing is ready
  // MENU_ITEMS_MAP.Billing,
  MENU_ITEMS_MAP.NotificationSettings,
];

export const colors: string[] = [
  '#201F20',
  '#7B61FF',
  '#5E4EAD',
  '#372499',
  '#FC7F7F',
  '#EF5656',
  '#CA3131',
  '#61C6FF',
  '#43A6DE',
  '#197FB8',
  '#B8B8B8',
  '#FFE168',
  '#EAC83D',
  '#D1AC1A',
  '#62F1AD',
  '#34D187',
  '#0FAB61',
  '#FFB661',
  '#E59639',
  '#BD7014',
  '#D7D7D7',
  '#FDA0FF',
  '#EF74F1',
  '#CF31D2',
  '#86A8FF',
  '#4372EF',
  '#214EC6',
  '#D783FF',
  '#B546EA',
  '#9D1EDA',
  '#EDEDED',
  '#ACEE78',
  '#80D240',
  '#5EAD20',
  '#FF965B',
  '#E97533',
  '#D94F00',
  '#FF9CD2',
  '#E161A6',
  '#D42783',
];

export const imageExtensions = ['jpg', 'jpeg', 'png', 'gif'];

export interface DayOfWeek {
  display: string;
  dayNumber: number;
}

export const DAYS_OF_WEEK: DayOfWeek[] = [
  {display: 'Monday', dayNumber: 0},
  {display: 'Tuesday', dayNumber: 1},
  {display: 'Wednesday', dayNumber: 2},
  {display: 'Thursday', dayNumber: 3},
  {display: 'Friday', dayNumber: 4},
  {display: 'Saturday', dayNumber: 5},
  {display: 'Sunday', dayNumber: 6},
];

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
export const ALLOWED_IMAGE_TYPES_WARNING = 'Unsupported image type. Allowed image types: JPEG, JPG, PNG, GIF';
export const IMAGE_UPLOAD_MAX_SIZE = 10; //mb

export const suffixOptions = [
  'units',
  'items',
  'reps',
  'sets',
  'times',
  'sessions',
  'steps',
  'meters',
  'miles',
  'grams',
  'kilograms',
  'pounds',
  'ounces',
  'milliliters',
  'liters',
  'cups',
  'servings',
  'calories',
  'pages',
  'tasks',
  'lessons',
  'rounds',
  'percent',
];
